/**
 * Upload queue.
 *
 * Per photo: sha256(original) → POST /photos (presign) → PUT original (single
 * XHR, or sequential MPU parts for files ≥100 MB) → PUT preview → POST /uploaded
 * confirm. Concurrency 3; each PUT retried 3× with exponential backoff. Progress
 * (0..100) is reported per photo via callbacks.
 *
 * In mock mode (api.isMock, or a `mock://` presigned URL) PUTs are simulated
 * with fake progress so the flow runs standalone.
 */

import { seedPricing } from '@rpl/pricing';
import type { Api } from './api';
import { ApiError } from './api';
// friendlyError is a pure switch with no pricing in scope, so the SEED carries the
// number. A stale seed can only mis-state a figure in an error message, never gate
// an upload: the real limit is enforced by the worker against LIVE pricing.
import { formatBytes } from './format';
import { sha256Hex } from './hash';
import type { OriginalUploadTarget, PhotoUiStatus } from './types';
import { gbCapLabel } from './uploadLimits';
import { delay, randomHex } from './util';

const CONCURRENCY = 3;
const RETRIES = 3;
/**
 * A PUT with NO progress for this long is a stalled connection (a phone
 * switching networks, a socket that dies without erroring): abort it so the
 * retry + error path runs instead of the photo sitting at "uploading" and
 * freezing Continue forever. Every progress event re-arms the clock, so a big
 * TIFF on a slow line that is still moving never times out.
 */
export const UPLOAD_STALL_MS = 60_000;

export interface UploadItem {
  /** Client photo id (React key). */
  localId: string;
  file: File;
  filename: string;
  bytes: number;
  width: number;
  height: number;
  previewBlob: Blob;
}

export interface UploadCallbacks {
  onStatus: (localId: string, status: PhotoUiStatus) => void;
  onProgress: (localId: string, progress: number) => void;
  onPhotoId: (localId: string, photoId: string) => void;
  onError: (localId: string, message: string) => void;
  onDone: (localId: string, photoId: string) => void;
}

export interface UploadContext {
  projectId: string;
  token: string;
  api: Api;
}

type PutResult = { etag: string | undefined };

export async function putWithProgress(
  url: string,
  body: Blob,
  onFraction: (fraction: number) => void,
  isMock: boolean,
): Promise<PutResult> {
  if (isMock || url.startsWith('mock://')) {
    const steps = 6;
    for (let i = 1; i <= steps; i++) {
      await delay(50 + Math.random() * 90);
      onFraction(i / steps);
    }
    return { etag: `"mock-${randomHex(6)}"` };
  }
  return new Promise<PutResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const disarm = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };
    const arm = (ms = UPLOAD_STALL_MS) => {
      disarm();
      stallTimer = setTimeout(() => {
        stallTimer = null;
        reject(new Error('Upload stalled'));
        xhr.abort();
      }, ms);
    };
    xhr.open('PUT', url);
    if (body.type) xhr.setRequestHeader('Content-Type', body.type);
    xhr.upload.onprogress = (e) => {
      arm();
      if (e.lengthComputable) onFraction(e.loaded / e.total);
    };
    // Body "sent": some browsers report this once the body is BUFFERED, and a
    // slow uplink then drains the rest with no events, so allow longer here.
    xhr.upload.onload = () => arm(UPLOAD_STALL_MS * 3);
    xhr.onprogress = () => arm(UPLOAD_STALL_MS * 3);
    xhr.onload = () => {
      disarm();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader('ETag') ?? undefined });
      } else {
        reject(new Error(`PUT failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => {
      disarm();
      reject(new Error('Network error during upload'));
    };
    xhr.ontimeout = () => {
      disarm();
      reject(new Error('Upload timed out'));
    };
    xhr.onabort = () => {
      disarm();
      reject(new Error('Upload aborted')); // no-op after a stall (already rejected)
    };
    xhr.send(body);
    arm();
  });
}

async function withRetry<T>(fn: () => Promise<T>, tries = RETRIES): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < tries - 1) {
        await delay(300 * 2 ** attempt + Math.random() * 200);
      }
    }
  }
  throw lastErr;
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'too_many_photos':
        return "That's over the photo limit for one order.";
      case 'order_too_large':
        // Derived from the seed, never hardcoded — the cap has moved once
        // already (5 GB -> 10 GB, 2026-08) and this copy silently went stale.
        return `This order is over the ${gbCapLabel(seedPricing.limits.maxOrderBytes)} `
          + 'upload limit. Start a second order for the rest.';
      case 'file_too_large':
        return `That file is over ${formatBytes(seedPricing.limits.maxFileBytes)}. Email us for custom orders.`;
      case 'not_an_image':
        return "This file isn't a supported image (JPEG, PNG, TIFF, HEIC, WebP). Re-save it as JPEG and try again.";
      case 'project_not_found':
        return 'This order session expired. Please restart.';
      default:
        return err.message || 'Upload failed. Please try again.';
    }
  }
  return 'Upload failed. Check your connection and try again.';
}

async function uploadOriginal(
  ctx: UploadContext,
  photoId: string,
  file: File,
  target: OriginalUploadTarget,
  onFraction: (fraction: number) => void,
): Promise<void> {
  if (target.mode === 'single') {
    await withRetry(() => putWithProgress(target.put_url, file, onFraction, ctx.api.isMock));
    return;
  }
  // Multipart: sequential parts, each ≥ part_size (last is the remainder).
  const partSize = target.part_size;
  const total = file.size;
  const numParts = Math.max(1, Math.ceil(total / partSize));
  const parts: Array<{ partNumber: number; etag: string }> = [];
  for (let n = 1; n <= numParts; n++) {
    const start = (n - 1) * partSize;
    const end = Math.min(start + partSize, total);
    const chunk = file.slice(start, end);
    const { put_url } = await ctx.api.getMpuPart(
      ctx.projectId,
      ctx.token,
      photoId,
      n,
      target.upload_id,
    );
    const res = await withRetry(() =>
      putWithProgress(
        put_url,
        chunk,
        (frac) => onFraction((start + frac * (end - start)) / total),
        ctx.api.isMock,
      ),
    );
    parts.push({ partNumber: n, etag: res.etag ?? '' });
  }
  await ctx.api.completeMpu(ctx.projectId, ctx.token, photoId, {
    uploadId: target.upload_id,
    parts,
  });
}

async function uploadOne(
  ctx: UploadContext,
  item: UploadItem,
  cb: UploadCallbacks,
): Promise<void> {
  cb.onStatus(item.localId, 'hashing');
  cb.onProgress(item.localId, 2);
  const sha256 = await sha256Hex(item.file);

  cb.onStatus(item.localId, 'uploading');
  const created = await ctx.api.createPhoto(ctx.projectId, ctx.token, {
    filename: item.filename,
    bytes: item.bytes,
    sha256,
    width: item.width,
    height: item.height,
  });
  cb.onPhotoId(item.localId, created.photo_id);

  // Original: 5 → 85 %
  await uploadOriginal(ctx, created.photo_id, item.file, created.original, (frac) =>
    cb.onProgress(item.localId, Math.round(5 + frac * 80)),
  );

  // Preview: 85 → 96 %
  await withRetry(() =>
    putWithProgress(
      created.preview_put_url,
      item.previewBlob,
      (frac) => cb.onProgress(item.localId, Math.round(85 + frac * 11)),
      ctx.api.isMock,
    ),
  );

  // Confirm: 96 → 100 %
  cb.onProgress(item.localId, 97);
  await ctx.api.confirmUploaded(ctx.projectId, ctx.token, created.photo_id);
  cb.onProgress(item.localId, 100);
  cb.onStatus(item.localId, 'ready');
  cb.onDone(item.localId, created.photo_id);
}

/** Bounded-concurrency upload queue. One instance lives per active project. */
export class UploadQueue {
  private queue: UploadItem[] = [];
  private active = 0;
  private cancelled = new Set<string>();

  constructor(
    private readonly ctx: UploadContext,
    private readonly cb: UploadCallbacks,
  ) {}

  enqueue(item: UploadItem): void {
    this.queue.push(item);
    this.pump();
  }

  /** Drop a not-yet-started item (best-effort; in-flight uploads still finish). */
  cancel(localId: string): void {
    this.cancelled.add(localId);
    this.queue = this.queue.filter((i) => i.localId !== localId);
  }

  private pump(): void {
    while (this.active < CONCURRENCY && this.queue.length > 0) {
      const item = this.queue.shift()!;
      if (this.cancelled.has(item.localId)) continue;
      this.active++;
      void uploadOne(this.ctx, item, this.cb)
        .catch((err) => {
          if (this.cancelled.has(item.localId)) return;
          this.cb.onError(item.localId, friendlyError(err));
          this.cb.onStatus(item.localId, 'error');
        })
        .finally(() => {
          this.active--;
          this.pump();
        });
    }
  }
}
